import { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, VStack, HStack, Button, useToast, Card, CardBody, Badge,
  Divider, Flex, Spinner, Textarea, Select,
} from '@chakra-ui/react';
import { apiService } from '../services/apiService';

const STATUT_LABELS = { nouveau: 'Nouveau', lu: 'Lu', traite: 'Traité' };
const STATUT_COLORS = { nouveau: 'orange', lu: 'blue', traite: 'green' };

export default function MessagesPasteur() {
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatut, setFilterStatut] = useState('all');
  const [reponses, setReponses] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.makeRequest('/messages-pasteur');
      setMessages(data.messages || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markAsLu = async (id) => {
    try {
      await apiService.makeRequest(`/messages-pasteur/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ statut: 'lu' }),
      });
      fetchMessages();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    }
  };

  const handleReply = async (id) => {
    const reponse = (reponses[id] || '').trim();
    if (!reponse) return;
    try {
      setSavingId(id);
      await apiService.makeRequest(`/messages-pasteur/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ reponse }),
      });
      toast({ title: 'Réponse envoyée', description: 'Le membre a été notifié par email.', status: 'success', duration: 4000 });
      setReponses(prev => ({ ...prev, [id]: '' }));
      fetchMessages();
    } catch (err) {
      toast({ title: 'Erreur', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setSavingId(null);
    }
  };

  const filtered = messages.filter(m => filterStatut === 'all' || m.statut === filterStatut);

  return (
    <Box maxW="900px" mx="auto">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="lg">Messages des membres</Heading>
        <Select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} maxW="200px" size="sm">
          <option value="all">Tous les statuts</option>
          <option value="nouveau">Nouveaux</option>
          <option value="lu">Lus</option>
          <option value="traite">Traités</option>
        </Select>
      </Flex>
      <Text color="gray.500" mb={6}>Messages envoyés directement par les membres depuis l'application.</Text>

      {loading ? (
        <Flex justify="center" py={10}><Spinner size="lg" /></Flex>
      ) : filtered.length === 0 ? (
        <Text color="gray.500" textAlign="center" py={8}>Aucun message pour le moment.</Text>
      ) : (
        <VStack spacing={4} align="stretch">
          {filtered.map(m => (
            <Card key={m.id} variant="outline" borderColor={m.statut === 'nouveau' ? 'orange.300' : 'gray.200'}>
              <CardBody>
                <Flex justify="space-between" align="start" mb={2}>
                  <Box>
                    <Text fontWeight="bold">{m.sujet}</Text>
                    <Text fontSize="sm" color="gray.600">
                      De {m.prenom} {m.nom} — {m.email}{m.telephone ? ` — ${m.telephone}` : ''}
                    </Text>
                  </Box>
                  <Badge colorScheme={STATUT_COLORS[m.statut]}>{STATUT_LABELS[m.statut]}</Badge>
                </Flex>
                <Text fontSize="sm" whiteSpace="pre-wrap" mb={2}>{m.contenu}</Text>
                <Text fontSize="xs" color="gray.400" mb={3}>
                  Envoyé le {new Date(m.date_envoi).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </Text>

                {m.reponse ? (
                  <>
                    <Divider my={2} />
                    <Text fontSize="sm" fontWeight="bold" color="green.700" mb={1}>Votre réponse :</Text>
                    <Text fontSize="sm" whiteSpace="pre-wrap">{m.reponse}</Text>
                  </>
                ) : (
                  <VStack align="stretch" spacing={2}>
                    {m.statut === 'nouveau' && (
                      <Button size="xs" variant="ghost" alignSelf="flex-start" onClick={() => markAsLu(m.id)}>
                        Marquer comme lu
                      </Button>
                    )}
                    <Textarea
                      size="sm"
                      placeholder="Écrire une réponse..."
                      value={reponses[m.id] || ''}
                      onChange={e => setReponses(prev => ({ ...prev, [m.id]: e.target.value }))}
                      rows={3}
                    />
                    <Button size="sm" colorScheme="green" alignSelf="flex-end"
                      onClick={() => handleReply(m.id)}
                      isLoading={savingId === m.id}
                      isDisabled={!(reponses[m.id] || '').trim()}>
                      Répondre
                    </Button>
                  </VStack>
                )}
              </CardBody>
            </Card>
          ))}
        </VStack>
      )}
    </Box>
  );
}
